import React from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from 'tss-react/mui';
import {createTheme, ThemeProvider} from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';

const theme = createTheme();
const useStyles = makeStyles()((theme) => ({
   drawer: {
      flexShrink: 0,
      whiteSpace: 'nowrap',
      [theme.breakpoints.up('lg')]: {
         marginTop: 64,
         height: 'calc(100% - 64px)'
      }
   },
   drawerOpen: {
      transition: theme.transitions.create('width', {
         easing: theme.transitions.easing.sharp,
         duration: theme.transitions.duration.enteringScreen
      })
   },
   drawerClose: {
      transition: theme.transitions.create('width', {
         easing: theme.transitions.easing.sharp,
         duration: theme.transitions.duration.leavingScreen
      }),
      overflowX: 'hidden',
      width: '57px',
      [theme.breakpoints.up('sm')]: {
         width: '73px'
      }
   },
   root: {
      backgroundColor: theme.palette.white,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      padding: '8px',
      maxWidth: '80%'
   },
   auto: {
      width: 'auto'
   },
   static: {
      width: 240
   },
   big: {
      width: '75%'
   },
   medium: {
      width: '50%'
   },
   small: {
      width: '30%'
   }
}));
/**
 * Barra lateral temporal simple que mostrara el contenido envuelto por este componente.
 *
 * Cualquier otra propiedad no especificada en la documentacion sera reenviada al componente [Drawer](https://material-ui.com/api/drawer/).
 */
function Sidebar({
   open,
   variant,
   onClose,
   className,
   children,
   keepMounted,
   size,
   disableBackdropClick,
   disableEscapeKeyDown,
   ...rest
}) {
   const {classes, cx} = useStyles();
   function handleOnClose(e, reason) {
      if (disableBackdropClick && reason === 'backdropClick') {
         return false;
      }

      if (disableEscapeKeyDown && reason === 'escapeKeyDown') {
         return false;
      }

      if (typeof onClose === 'function') {
         onClose(e, reason);
      }
   }

   return (
      <Drawer
         classes={{
            paper: cx(classes.drawer, classes[size], className, {
               [classes.drawerOpen]: open,
               [classes.drawerClose]: !open
            })
         }}
         onClose={handleOnClose}
         open={open}
         variant={variant}
         /* ModalProps={{
            BackdropProps: {
               invisible: true
            },
            keepMounted
         }} */
         hideBackdrop={true}
         keepMounted={keepMounted}
         {...rest}
      >
         <div
            className={cx(classes.root, {
               [classes.drawerOpen]: open,
               [classes.drawerClose]: !open
            })}
         >
            {children}
         </div>
      </Drawer>
   );
}

Sidebar.propTypes = {
   /**
    * Si es __true__ la barra lateral sera visible.
    */
   open: PropTypes.bool.isRequired,
   /**
    * Funcion a ejecutar cuando se desea cerrar/ocultar la barra lateral. Generalmen usado para modificar `open` a __false__.
    */
   onClose: PropTypes.func,
   /**
    * Tamaño que ocupara la barra lateral:
    *
    * - __auto__: El ancho de la barra lateral se adaptara al contenido.
    * - __static__: 240px
    * - __big__: 75% del contenedor de la barra lateral.
    * - __medium__: 50% del contenedor de la barra lateral.
    * - __small__: 30% del contenedor de la barra lateral.
    */
   size: PropTypes.oneOf(['static', 'big', 'medium', 'small', 'auto']),
   /**
    * Si es ___true__, mantendra los componentes en el DOM.
    */
   keepMounted: PropTypes.bool,
   /**
    * Variante de Material-UI a usar.
    */
   variant: PropTypes.oneOf(['permanent', 'persistent', 'temporary']),
   /**
    * Si es __true__ no se ejecutara el callback `onClose` cuando se haga click fuera del modal.
    */
   disableBackdropClick: PropTypes.bool,
   /**
    * Si es __true__ no se ejecutara el callback `onClose` cuando se presione la tecla *escape*.
    */
   disableEscapeKeyDown: PropTypes.bool
};

Sidebar.defaultProps = {
   size: 'auto',
   keepMounted: false,
   default: 'temporary',
   disableBackdropClick: false,
   disableEscapeKeyDown: false
};

export default Sidebar;
